import { useState, useEffect, useMemo } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { productService } from '../services/productos/productService'
import { assetService } from '../services/activos/assetService'
import { ProductFiltersBar } from './filters/ProductFiltersBar'

export function AssetFormModal({ 
  isOpen, 
  mode, // 'create' | 'edit'
  assetId, // ID del activo a editar (null para crear)
  parentId, // ID del activo padre (usado cuando se crea un hijo)
  onClose, 
  onSuccess,
  onCreate,
  onUpdate
}) {
  const [formData, setFormData] = useState({
    product_id: '',
    location: '',
    tech_code: '',
    code: '',
    sn: '',
    sap_code: '',
    parent_id: null
  })
  const [formErrors, setFormErrors] = useState({})
  const [localModalError, setLocalModalError] = useState(null)
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterProductType, setFilterProductType] = useState('')
  const [filterManufacturer, setFilterManufacturer] = useState('')
  const [filterFamily, setFilterFamily] = useState('')

  // Obtener lista de productos
  const { data: products = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts(),
    enabled: isOpen,
  })

  // Cargar datos del activo si estamos en modo edición
  const { data: assetData, isLoading: isLoadingAsset } = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => assetService.getAsset(assetId),
    enabled: isOpen && mode === 'edit' && !!assetId,
  })

  // Inicializar formulario cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setLocalModalError(null)
      setFormErrors({})
      setSearchQuery('')
      setFilterProductType('')
      setFilterManufacturer('')
      setFilterFamily('')
      
      if (mode === 'create') {
        // Modo crear: usar parentId si está disponible
        setFormData({
          product_id: '',
          location: '',
          tech_code: '',
          code: '',
          sn: '',
          sap_code: '',
          parent_id: parentId || null
        })
      } else if (mode === 'edit' && assetData) {
        // Modo editar: cargar datos del activo
        setFormData({
          product_id: assetData.Product?.ID || assetData.ProductID || '',
          location: assetData.Location || '',
          tech_code: assetData.TechCode || '',
          code: assetData.Code || '',
          sn: assetData.Sn || '',
          sap_code: assetData.SapCode || '',
          parent_id: assetData.ParentID || null
        })
      }
    }
  }, [isOpen, mode, parentId, assetData])

  const uniqueProductTypes = useMemo(() => {
    return [...new Set(products.map((p) => p.product_type || p.ProductType).filter(Boolean))].sort()
  }, [products])

  const uniqueManufacturers = useMemo(() => {
    return [...new Set(products.map((p) => p.manufacturer || p.Manufacturer).filter(Boolean))].sort()
  }, [products])

  const uniqueFamilies = useMemo(() => {
    return [...new Set(products.map((p) => p.family || p.Family).filter(Boolean))].sort()
  }, [products])

  const filteredProducts = useMemo(() => {
    let filtered = products

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((product) =>
        (product.name || product.Name || '').toLowerCase().includes(query) ||
        (product.product_type || product.ProductType || '').toLowerCase().includes(query) ||
        (product.manufacturer || product.Manufacturer || '').toLowerCase().includes(query) ||
        (product.family || product.Family || '').toLowerCase().includes(query) ||
        (product.description || product.Description || '').toLowerCase().includes(query)
      )
    }

    if (filterProductType) {
      filtered = filtered.filter((product) => (product.product_type || product.ProductType) === filterProductType)
    }

    if (filterManufacturer) {
      filtered = filtered.filter((product) => (product.manufacturer || product.Manufacturer) === filterManufacturer)
    }

    if (filterFamily) {
      filtered = filtered.filter((product) => (product.family || product.Family) === filterFamily)
    }

    return filtered
  }, [products, searchQuery, filterProductType, filterManufacturer, filterFamily])

  const validateForm = () => {
    const errors = {}
    
    if (!formData.product_id || formData.product_id === '') {
      errors.product_id = 'El producto es requerido'
    }
    
    if (!formData.location.trim()) {
      errors.location = 'La ubicación es requerida'
    }
    
    if (!formData.tech_code || formData.tech_code === '') {
      errors.tech_code = 'El código técnico es requerido'
    }
    
    if (!formData.code.trim()) {
      errors.code = 'El código es requerido'
    }
    
    if (!formData.sn.trim()) {
      errors.sn = 'El número de serie es requerido'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLocalIsSubmitting(true)
    setLocalModalError(null)

    try {
      const assetPayload = {
        product_id: parseInt(formData.product_id),
        location: formData.location.trim(),
        tech_code: parseInt(formData.tech_code),
        code: formData.code.trim(),
        sn: formData.sn.trim(),
        sap_code: formData.sap_code.trim() || undefined,
      }

      // Solo incluir parent_id si está presente
      if (formData.parent_id) {
        assetPayload.parent_id = parseInt(formData.parent_id)
      }

      if (mode === 'create') {
        if (onCreate) {
          await onCreate(assetPayload)
        } else {
          await assetService.createAsset(assetPayload)
        }
      } else if (mode === 'edit' && assetId) {
        if (onUpdate) {
          await onUpdate({ id: assetId, data: assetPayload })
        } else {
          await assetService.updateAsset(assetId, assetPayload)
        }
      }

      onSuccess()
      onClose()
    } catch (error) {
      // Manejar errores del backend con mensajes específicos
      let errorMessage = error.response?.data?.message || error.message || 'Error al guardar el activo'
      
      // Mensajes más amigables para errores comunes
      if (errorMessage.includes('producto') && errorMessage.includes('no encontrado')) {
        errorMessage = 'El producto seleccionado no existe. Por favor, seleccione otro producto.'
      } else if (errorMessage.includes('activo padre') && errorMessage.includes('no encontrado')) {
        errorMessage = 'El activo padre seleccionado no existe.'
      } else if (errorMessage.includes('no se puede cambiar el padre o el código de un activo que tiene hijos')) {
        errorMessage = 'No se puede modificar el padre o el código de un activo que tiene activos hijos asociados.'
      }
      
      setLocalModalError(errorMessage)
    } finally {
      setLocalIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  if (!isOpen) return null

  const isLoading = isLoadingProducts || (mode === 'edit' && isLoadingAsset)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">
            {mode === 'create' ? 'Crear Activo' : 'Editar Activo'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            disabled={localIsSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* General error message */}
            {localModalError && (
              <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                <p className="text-red-300 text-sm">{localModalError}</p>
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-blue-500" size={32} />
              </div>
            ) : (
              <>
                {/* Parent ID (solo mostrar, no editable) */}
                {formData.parent_id && (
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-1">Activo Padre</p>
                    <p className="text-white font-medium">ID: {formData.parent_id}</p>
                    <p className="text-xs text-gray-500 mt-1">Este activo será hijo del activo seleccionado</p>
                  </div>
                )}

                {/* Product Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Producto <span className="text-red-400">*</span>
                  </label>
                  <div className="mb-3">
                    <ProductFiltersBar
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      filterProductType={filterProductType}
                      setFilterProductType={setFilterProductType}
                      filterManufacturer={filterManufacturer}
                      setFilterManufacturer={setFilterManufacturer}
                      filterFamily={filterFamily}
                      setFilterFamily={setFilterFamily}
                      uniqueProductTypes={uniqueProductTypes}
                      uniqueManufacturers={uniqueManufacturers}
                      uniqueFamilies={uniqueFamilies}
                    />
                    <p className="mt-2 text-xs text-gray-400">
                      {filteredProducts.length} {filteredProducts.length === 1 ? 'producto disponible' : 'productos disponibles'}
                    </p>
                  </div>
                  <select
                    name="product_id"
                    value={formData.product_id}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.product_id ? 'border-red-500' : 'border-gray-600'
                    }`}
                    disabled={localIsSubmitting || isLoadingProducts}
                  >
                    <option value="">Seleccione un producto</option>
                    {filteredProducts.map(product => (
                      <option key={product.id || product.ID} value={product.id || product.ID}>
                        {product.name || product.Name} {product.manufacturer || product.Manufacturer ? `- ${product.manufacturer || product.Manufacturer}` : ''}
                      </option>
                    ))}
                  </select>
                  {formErrors.product_id && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.product_id}</p>
                  )}
                </div>

                {/* Location and Tech Code */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Ubicación <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.location ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Ubicación"
                      disabled={localIsSubmitting}
                    />
                    {formErrors.location && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.location}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Código Técnico <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      name="tech_code"
                      value={formData.tech_code}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.tech_code ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Código técnico"
                      disabled={localIsSubmitting}
                    />
                    {formErrors.tech_code && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.tech_code}</p>
                    )}
                  </div>
                </div>

                {/* Code and Serial Number */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Código <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.code ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Código"
                      disabled={localIsSubmitting}
                    />
                    {formErrors.code && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.code}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Número de Serie <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="sn"
                      value={formData.sn}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.sn ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Número de serie"
                      disabled={localIsSubmitting}
                    />
                    {formErrors.sn && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.sn}</p>
                    )}
                  </div>
                </div>

                {/* SAP Code (opcional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Código SAP
                  </label>
                  <input
                    type="text"
                    name="sap_code"
                    value={formData.sap_code}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Código SAP (opcional)"
                    disabled={localIsSubmitting}
                  />
                </div>
              </>
            )}
          </div>

          {/* Footer with buttons */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={localIsSubmitting || isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              disabled={localIsSubmitting || isLoading}
            >
              {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Crear' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
